import { z } from 'zod';
import { AddressLite } from './threads.js';

export const MailRecoveryRetentionDays = z.union([
  z.literal(0),
  z.literal(7),
  z.literal(14),
  z.literal(21),
  z.literal(28),
]);
export type MailRecoveryRetentionDays = z.infer<typeof MailRecoveryRetentionDays>;

export const MailRecoverySettings = z.object({
  retentionDays: MailRecoveryRetentionDays,
  recoverableMessages: z.number().int().nonnegative(),
  nextPurgeAt: z.number().int().nonnegative().nullable(),
  exactSourceMessages: z.number().int().nonnegative(),
  exactSourceBytes: z.number().int().nonnegative(),
});
export type MailRecoverySettings = z.infer<typeof MailRecoverySettings>;

export const MailRecoverySettingsUpdate = z.object({
  retentionDays: MailRecoveryRetentionDays,
});
export type MailRecoverySettingsUpdate = z.infer<typeof MailRecoverySettingsUpdate>;

export const MailRecoveryReason = z.enum([
  'provider_deleted',
  'permanent_delete',
  'folder_removed',
]);
export type MailRecoveryReason = z.infer<typeof MailRecoveryReason>;

export const MailRecoveryRestoreState = z.enum(['available', 'restoring', 'failed']);
export type MailRecoveryRestoreState = z.infer<typeof MailRecoveryRestoreState>;

export const MailRecoveryMessageSummary = z.object({
  id: z.number().int().positive(),
  accountId: z.number().int().positive(),
  accountName: z.string(),
  accountEmail: z.string(),
  accountColor: z.string(),
  subject: z.string(),
  from: z.array(AddressLite),
  date: z.number().int(),
  removedAt: z.number().int().nonnegative(),
  expiresAt: z.number().int().nonnegative(),
  originalFolderName: z.string(),
  originalFolderId: z.number().int().positive().nullable(),
  reason: MailRecoveryReason,
  snippet: z.string(),
  hasAttachments: z.boolean(),
  attachmentCount: z.number().int().nonnegative(),
  restorable: z.boolean(),
  exactSource: z.boolean(),
  restoreState: MailRecoveryRestoreState,
  restoreError: z.string().nullable(),
});
export type MailRecoveryMessageSummary = z.infer<typeof MailRecoveryMessageSummary>;

export const MailRecoveryListResponse = z.object({
  rows: z.array(MailRecoveryMessageSummary),
  nextCursor: z.string().nullable(),
});
export type MailRecoveryListResponse = z.infer<typeof MailRecoveryListResponse>;

export const MailRecoveryAttachment = z.object({
  id: z.number().int().positive(),
  filename: z.string().nullable(),
  contentType: z.string(),
  size: z.number().int().nonnegative(),
  isInline: z.boolean(),
  available: z.boolean(),
});
export type MailRecoveryAttachment = z.infer<typeof MailRecoveryAttachment>;

export const MailRecoveryMessageDetail = MailRecoveryMessageSummary.extend({
  to: z.array(AddressLite),
  cc: z.array(AddressLite),
  replyTo: z.array(AddressLite),
  html: z.string().nullable(),
  text: z.string().nullable(),
  attachments: z.array(MailRecoveryAttachment),
});
export type MailRecoveryMessageDetail = z.infer<typeof MailRecoveryMessageDetail>;

export const MailRecoveryRestoreRequest = z.object({
  targetFolderId: z.number().int().positive(),
});
export type MailRecoveryRestoreRequest = z.infer<typeof MailRecoveryRestoreRequest>;

export const MailRecoveryMutationResponse = z.object({
  ok: z.literal(true),
  id: z.number().int().positive().optional(),
  state: MailRecoveryRestoreState.optional(),
});
export type MailRecoveryMutationResponse = z.infer<typeof MailRecoveryMutationResponse>;
